// The barrel, not `entities/step` directly: `shooting-day.ts` extends `Step`, and in an isolated
// module graph (this module's own unit test) reaching `step.ts` first races that subclass against
// its base class body. `entities/index.ts` fixes the order. Same fix as the two calendar routes.
import { Step } from '../../../app/entities';

import type { StepStatus } from '../../../app/entities/step';

import type { PlainDate } from '../../../core/services/plain-date';
import type { ContextMenuEntry, ContextMenuItem } from '@producer/ui-kit';

/** The subset of `Member` this menu needs — a name to show and an id to write. */
export type StepMenuCollaborator = { _id: string; fullName: string };

/** Every field the menu can write in one call, so one caller-side function owns persistence. */
export type StepMenuUpdate = {
  status?: StepStatus;
  assigneeId?: string | null;
  startDate?: PlainDate | null;
  dueDate?: PlainDate | null;
};

/**
 * PRO-1073. What the menu DOES is deliberately not this module's business: persistence, the
 * confirm dialog, the date modal, the navigation and the failure toast are all effects a component
 * owns, so the item list itself stays a pure function of the step and is testable without a DOM,
 * a router, a modal outlet or a MobX store.
 */
export type StepMenuHandlers = {
  onOpen: () => void;
  onEditDate: (field: 'startDate' | 'dueDate') => void;
  onRename: () => void;
  onDelete: () => void;
  onUpdate: (values: StepMenuUpdate) => void;
};

/**
 * The right-click menu for one step bar in the Calendar view (PRO-1073's job to be done).
 *
 * A view-only (guest) member gets exactly one entry — `Open step…`. Every other entry writes, so
 * rather than rendering them disabled (a menu of dead rows reads as a bug), they are dropped and
 * `ContextMenu`'s own separator collapsing removes the dividers left behind.
 */
export const buildStepMenuItems = ({
  step,
  isViewOnly,
  collaborators,
  handlers,
}: {
  step: Step;
  isViewOnly?: boolean;
  collaborators: StepMenuCollaborator[];
  handlers: StepMenuHandlers;
}): ContextMenuEntry[] => {
  const { onOpen, onEditDate, onRename, onDelete, onUpdate } = handlers;

  const openStep: ContextMenuItem = { title: 'Open step…', onSelect: () => onOpen() };

  if (isViewOnly) return [openStep];

  const statusItems: ContextMenuItem[] = Object.entries(Step.StatusMapper).map(
    ([status, { title }]) => ({
      title,
      onSelect: () => onUpdate({ status: status as StepStatus }),
    }),
  );

  const assigneeItems: ContextMenuItem[] = [
    { title: 'No assignee', onSelect: () => onUpdate({ assigneeId: null }) },
    ...collaborators.map((collaborator) => ({
      title: collaborator.fullName,
      onSelect: () => onUpdate({ assigneeId: collaborator._id }),
    })),
  ];

  return [
    openStep,
    { separator: true },
    { title: 'Status', subItems: statusItems },
    { title: 'Assignee', subItems: assigneeItems },
    { title: 'Rename…', onSelect: () => onRename() },
    { title: 'Start date…', onSelect: () => onEditDate('startDate') },
    { title: 'Due date…', onSelect: () => onEditDate('dueDate') },
    { separator: true },
    // Dropped, not `hidden`, for a step with neither date: nothing to remove, so the entry would
    // be a no-op that still looks like an action. Returning only the entries actually offered
    // keeps this function's output the menu it describes, and `ContextMenu` collapses the divider
    // this can leave dangling. Such a step is still reachable from the timeline's undated area
    // (`empty-row-add.tsx`).
    ...(step.startDate || step.dueDate
      ? [
          {
            title: 'Remove from timeline',
            onSelect: () => onUpdate({ startDate: null, dueDate: null }),
          } satisfies ContextMenuItem,
        ]
      : []),
    { title: 'Delete', type: 'danger', onSelect: () => onDelete() },
  ];
};
